import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { UserCog, Plus, KeyRound, Camera, ShieldOff, ShieldCheck, X, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from '../../lib/docs';
import { TimezoneSelect } from '../../components/TimezoneSelect';

const SUGGESTED_LANGS = ['English', 'French', 'Spanish', 'Vietnamese', 'Mandarin', 'Portuguese'];

export default function AdminTaxSpecialistsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // specialist record being edited
  const [allLangs, setAllLangs] = useState(SUGGESTED_LANGS);

  const load = async () => {
    setLoading(true);
    try {
      const [list, langs] = await Promise.all([api.adminListSpecialists(), api.myLanguages()]);
      setRows(list);
      setAllLangs(Array.from(new Set([...SUGGESTED_LANGS, ...(langs || [])])));
    } catch { toast.error('Could not load tax specialists'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout
      title="Tax Specialists"
      subtitle="Create, manage and assign tax specialists to members."
      actions={
        <button onClick={() => setCreating(true)} data-testid="open-create-specialist"
                className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2 rounded-md text-[13px]">
          <Plus className="w-4 h-4" /> New specialist
        </button>
      }
    >
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">Loading…</p>
          : rows.length === 0 ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">No tax specialists yet.</p>
          : <table className="w-full text-[13.5px]" data-testid="specialists-table">
              <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-5 py-2.5">Name</th>
                  <th className="text-left px-5 py-2.5">Email</th>
                  <th className="text-left px-5 py-2.5">Title</th>
                  <th className="text-left px-5 py-2.5">Languages</th>
                  <th className="text-right px-5 py-2.5">Assigned</th>
                  <th className="text-center px-5 py-2.5">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {s.profile_picture_url
                          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${s.profile_picture_url}`} alt={s.name} className="w-9 h-9 rounded-full object-cover border border-neutral-200" />
                          : <div className="w-9 h-9 rounded-full bg-[#f5b300]/15 text-[#c89200] font-bold flex items-center justify-center">{(s.name || 'S').slice(0, 1)}</div>}
                        <span className="font-semibold text-[#14110f]">{s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim()}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-neutral-600">{s.email}</td>
                    <td className="px-5 py-3 text-neutral-700">{s.title || '—'}</td>
                    <td className="px-5 py-3 text-neutral-600 text-[12.5px]">{(s.languages || []).join(', ') || '—'}</td>
                    <td className="px-5 py-3 text-right">{s.assigned_members_count || 0}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${
                        s.active === false ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
                      }`}>
                        {s.active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setEditing(s)} className="text-[12px] text-[#c89200] hover:underline font-semibold">Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>

      {creating && <CreateModal allLangs={allLangs} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {editing && <EditModal specialist={editing} allLangs={allLangs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </AdminLayout>
  );
}

function CreateModal({ allLangs, onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    phone: '', title: '', bio: '', timezone: 'America/Edmonton', languages: [],
  });

  const submit = async () => {
    if (!form.email || !form.password || form.password.length < 8 || !form.first_name) {
      return toast.error('Email, first name and an 8+ char password are required');
    }
    try {
      setBusy(true);
      await api.adminCreateSpecialist(form);
      toast.success('Tax specialist created');
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create'); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Create tax specialist">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextField label="First name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="create-first" />
        <TextField label="Last name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="create-last" />
        <TextField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="create-email" />
        <TextField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="create-title" />
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-neutral-500">Timezone</span>
          <div className="mt-1">
            <TimezoneSelect value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })}
                            dataTestid="create-timezone" />
          </div>
        </label>
        <TextField label="Initial password (8+ chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="create-password" />
      </div>
      <TextArea label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} rows={3} />
      <LanguagePicker allLangs={allLangs} selected={form.languages} onChange={(v) => setForm({ ...form, languages: v })} />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-md border border-neutral-200 text-[13px]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="create-submit"
                className="px-5 py-2 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold text-[13px] inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create specialist
        </button>
      </div>
    </Modal>
  );
}

function EditModal({ specialist, allLangs, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: specialist.first_name || '',
    last_name: specialist.last_name || '',
    phone: specialist.phone || '',
    title: specialist.title || '',
    bio: specialist.bio || '',
    timezone: specialist.timezone || 'America/Edmonton',
    languages: specialist.languages || [],
    office_location: specialist.office_location || '',
    certifications: specialist.certifications || '',
    active: specialist.active !== false,
  });
  const [resetPw, setResetPw] = useState('');
  const fileRef = useRef();

  const save = async () => {
    try {
      setBusy(true);
      await api.adminUpdateSpecialist(specialist.id, form);
      toast.success('Saved');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!resetPw || resetPw.length < 8) return toast.error('New password must be 8+ chars');
    try {
      await api.adminResetSpecialistPassword(specialist.id, resetPw);
      toast.success('Password reset');
      setResetPw('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reset'); }
  };

  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      await api.adminUploadSpecialistPicture(specialist.id, fd);
      toast.success('Picture updated');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const deactivate = async () => {
    if (!confirm('Deactivate this specialist? They will lose login access but their assignments and audit history remain.')) return;
    try {
      await api.adminDeactivateSpecialist(specialist.id);
      toast.success('Deactivated');
      onSaved?.();
    } catch { toast.error('Could not deactivate'); }
  };

  return (
    <Modal onClose={onClose} title={specialist.name || 'Tax specialist'} subtitle={specialist.email}>
      <div className="flex items-center gap-4 mb-5 p-3 rounded-lg bg-neutral-50 border border-neutral-100">
        {specialist.profile_picture_url
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${specialist.profile_picture_url}`} alt="" className="w-14 h-14 rounded-full object-cover border border-neutral-200" />
          : <div className="w-14 h-14 rounded-full bg-[#f5b300]/15 text-[#c89200] font-bold text-[18px] flex items-center justify-center">{(specialist.name || 'S').slice(0, 1)}</div>}
        <div className="flex-1">
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-[12.5px] text-[#c89200] hover:underline font-semibold">
            <Camera className="w-3.5 h-3.5" /> Upload new picture
          </button>
          <p className="text-[11px] text-neutral-500 mt-0.5">JPG / PNG / WebP up to 4 MB</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextField label="First name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} />
        <TextField label="Last name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
        <TextField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-neutral-500">Timezone</span>
          <div className="mt-1">
            <TimezoneSelect value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })}
                            dataTestid="edit-timezone" />
          </div>
        </label>
        <TextField label="Office location" value={form.office_location} onChange={(v) => setForm({ ...form, office_location: v })} />
      </div>
      <TextArea label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} rows={3} />
      <TextField label="Certifications" value={form.certifications} onChange={(v) => setForm({ ...form, certifications: v })} />
      <LanguagePicker allLangs={allLangs} selected={form.languages} onChange={(v) => setForm({ ...form, languages: v })} />
      <label className="mt-3 inline-flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
        Active (can sign in)
      </label>

      <div className="mt-5 pt-5 border-t border-neutral-100 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Reset password</p>
          <div className="flex gap-2">
            <input type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password (8+ chars)"
                   className="flex-1 px-3 py-2.5 rounded-md border border-neutral-200 text-[13px]" data-testid="reset-pw-input" />
            <button onClick={reset} data-testid="reset-pw-btn" className="px-3 py-2 rounded-md bg-neutral-900 text-white text-[12.5px] inline-flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        </div>
        <div className="md:text-right md:self-end">
          {form.active && (
            <button onClick={deactivate} className="inline-flex items-center gap-1.5 text-[12.5px] text-rose-600 hover:underline font-semibold">
              <ShieldOff className="w-3.5 h-3.5" /> Deactivate account
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-md border border-neutral-200 text-[13px]">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="edit-save"
                className="px-5 py-2 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold text-[13px] inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
        </button>
      </div>
    </Modal>
  );
}

// ---- shared inputs ----
function Modal({ children, title, subtitle, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mt-10 mb-10 p-6 text-[#14110f]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-[18px] font-bold">{title}</h3>
            {subtitle && <p className="text-[12.5px] text-neutral-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="block mt-3">
      <span className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
    </label>
  );
}

function LanguagePicker({ allLangs, selected, onChange }) {
  const [custom, setCustom] = useState('');
  const toggle = (l) => {
    const set = new Set(selected);
    if (set.has(l)) set.delete(l); else set.add(l);
    onChange(Array.from(set));
  };
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!selected.includes(v)) onChange([...selected, v]);
    setCustom('');
  };
  const merged = Array.from(new Set([...allLangs, ...selected]));
  return (
    <div className="mt-3">
      <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Languages spoken</p>
      <div className="flex flex-wrap gap-2" data-testid="lang-picker">
        {merged.map((l) => {
          const on = selected.includes(l);
          return (
            <button key={l} onClick={() => toggle(l)} type="button"
                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold border transition ${
                      on ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f]' : 'bg-white border-neutral-200 text-neutral-700 hover:border-[#f5b300]/60'
                    }`}>
              {l}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustom())}
               placeholder="Add another language…"
               className="flex-1 px-3 py-2 rounded-md border border-neutral-200 text-[12.5px]" data-testid="lang-custom" />
        <button onClick={addCustom} type="button" className="px-3 py-2 rounded-md bg-neutral-900 text-white text-[12.5px]">Add</button>
      </div>
    </div>
  );
}
